import { Check, AlertTriangle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { passesDataGates, stateLabel, formatMde, formatZScore } from '@/lib/statistics';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';

interface SignificanceBadgeProps {
  zScore: number | null;
  isSignificant: boolean;
  preImpressions: number;
  postImpressions: number;
  preClicks: number;
  postClicks: number;
  preDaysWithData: number;
  postDaysWithData: number;
  effectiveWindowDays: number;
  mdeRelativePct: number | null;
}

export function SignificanceBadge({
  zScore, isSignificant,
  preImpressions, postImpressions, preClicks, postClicks,
  preDaysWithData, postDaysWithData, effectiveWindowDays,
  mdeRelativePct,
}: SignificanceBadgeProps) {
  const { i18n, t } = useTranslation('performance');
  const locale = (i18n.language?.startsWith('de') ? 'de' : 'en') as 'de' | 'en';

  const minDays = Math.ceil(effectiveWindowDays * 0.7);
  const gatesPass = passesDataGates({
    pre_impressions: preImpressions,
    post_impressions: postImpressions,
    pre_clicks: preClicks,
    post_clicks: postClicks,
    effective_window_days: effectiveWindowDays,
    pre_days_with_data: preDaysWithData,
    post_days_with_data: postDaysWithData,
  });
  const finalState = !gatesPass ? 'insufficient' : (isSignificant ? 'significant' : 'noise');

  const config = {
    significant: { icon: Check, classes: 'bg-success/10 text-success-ink border-success/30' },
    noise: { icon: AlertTriangle, classes: 'bg-warning/10 text-warning-ink border-warning/30' },
    insufficient: { icon: Lock, classes: 'bg-muted text-muted-foreground border-border' },
  } as const;
  const { icon: Icon, classes } = config[finalState];
  const label = stateLabel(finalState, locale);

  // Build the tooltip-reasons list. These describe WHY a gate failed, not the eventual badge state.
  const reasons: string[] = [];
  if (effectiveWindowDays < 7)
    reasons.push(t('tooltip.insufficientDays', 'Less than 7 full days post-optimization'));
  if (preImpressions < 100 || postImpressions < 100)
    reasons.push(t('tooltip.lowImpressions', 'Need ≥100 impressions on both sides'));
  if (preClicks < 5 || postClicks < 5)
    reasons.push(t('tooltip.lowClicks', 'Need ≥5 clicks on both sides'));
  if (effectiveWindowDays >= 7 && (preDaysWithData < minDays || postDaysWithData < minDays))
    reasons.push(t('tooltip.lowCoverage', {
      defaultValue: `Need data on ≥${minDays} of ${effectiveWindowDays} days (have ${preDaysWithData}/${postDaysWithData})`,
      minDays,
      windowDays: effectiveWindowDays,
      preDays: preDaysWithData,
      postDays: postDaysWithData,
    }));
  if (gatesPass && !isSignificant)
    reasons.push(t('tooltip.notSignificant', 'Difference within statistical noise (95% CI)'));

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* `data-testid` traegt hier eine Pruefung, nicht nur einen Selektor:
            der Haken ist die EINZIGE begruendete Verdikt-Ausnahme der ganzen
            Wirkungs-Seite (Begruendung im Inventar von
            directionHonesty.sweep.test.tsx). Der flaechige Symbolscan dort
            verbietet `lucide-check` ueberall SONST und braucht dafuer eine
            belastbare Adresse. Ueber Klassen wie `text-xs font-medium` zu
            erkennen, welches Pill gemeint ist, waere geraten: der
            Stabilitaets-Chip daneben ist bis auf zwei Utilities gleich gebaut. */}
        <span
          data-testid="significance-badge"
          className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border', classes)}
        >
          <Icon className="h-3 w-3" />
          <span>{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="space-y-1 text-xs">
          {zScore !== null && (
            <div>z = {formatZScore(zScore)}</div>
          )}
          {mdeRelativePct !== null && (
            <div>{t('tooltip.mde', 'Detectable effect')}: {formatMde(mdeRelativePct)}</div>
          )}
          <div>
            {t('tooltip.coverage', 'Coverage')}: {preDaysWithData}/{effectiveWindowDays} pre · {postDaysWithData}/{effectiveWindowDays} post
          </div>
          {reasons.length > 0 ? (
            <ul className="list-disc pl-4 mt-1">{reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
          ) : (
            <div className="mt-1">{t('tooltip.significant', 'Statistically significant change (p < 0.05)')}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
